/* global React */

function Modal({ open, onClose, children }) {
  if (!open) return null;
  return (
    <div style={{
      position: 'absolute', inset: 0, zIndex: 300,
      background: 'rgba(7, 13, 28, 0.8)', backdropFilter: 'blur(8px)',
      animation: 'fade-up 0.3s ease',
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        background: 'var(--ink)',
        borderTop: '1px solid rgba(255,46,136,0.35)',
        borderRadius: '24px 24px 0 0',
        padding: 22, paddingBottom: 40,
        maxHeight: '88%', overflow: 'auto',
        animation: 'fade-up 0.35s cubic-bezier(0.2, 0.9, 0.3, 1)',
      }}>
        <div style={{ width: 36, height: 3, background: 'rgba(237,228,211,0.2)', borderRadius: 2, margin: '0 auto 20px' }} />
        {children}
      </div>
    </div>
  );
}

function ModalLabel({ children }) {
  return <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.3em', color: 'var(--bone-dim)', marginBottom: 10, textTransform: 'uppercase' }}>{children}</div>;
}

// ─────────────────────────────────────────────────────────────
// Links back to the main site.
// This app carries only a slice of the content — the blog series and the core
// landing pages live on theoldschoolyard.com and are what actually rank. Once
// mobile visitors land here instead, those pages lose their route in from the
// front door, so every sheet points at its matching page.
//
// rel="noopener", NOT "noreferrer": these are our own links to our own domain,
// and noreferrer strips the Referer header, which would hide all of this
// traffic from analytics on the main site. noopener alone is the safe part.
// Slugs verified live (200, no redirect) on 11 Aug 2026 — /karaoke-rooms/
// 301s to /karaoke-rooms-london/, so always use the latter.
// ─────────────────────────────────────────────────────────────
const MAIN_SITE = 'https://theoldschoolyard.com/';

function SiteLink({ slug, title, note, accent = '#2e9bff' }) {
  return (
    <a href={MAIN_SITE + slug} target="_blank" rel="noopener" style={{
      display: 'block', padding: '11px 14px', borderRadius: 10, marginTop: 12,
      border: '1px solid ' + accent + '55', background: accent + '14',
      color: 'var(--bone)', textDecoration: 'none',
    }}>
      <span style={{ fontFamily: 'var(--sans)', fontWeight: 600, fontSize: 13.5 }}>{title} →</span>
      {note && <span style={{ display: 'block', fontFamily: 'var(--sans)', fontSize: 11.5, color: 'var(--bone-dim)', marginTop: 2 }}>{note}</span>}
    </a>
  );
}

function BookingModal({ open, onClose, accent = '#ff2e88' }) {
  const [party, setParty] = React.useState(4);
  const [day, setDay] = React.useState('Fri');
  const [time, setTime] = React.useState('20:00');
  const days = ['Tue','Wed','Thu','Fri','Sat'];
  const times = ['17:30','18:00','18:30','19:00','19:30','20:00','20:30','21:00','21:30','22:00'];

  return (
    <Modal open={open} onClose={onClose}>
      <div style={{ color: 'var(--bone)' }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.3em', color: accent, marginBottom: 6 }}>BOOK A TABLE</div>
          <div style={{ fontFamily: 'var(--display)', fontWeight: 800, letterSpacing: '-0.03em', fontSize: 26, lineHeight: 1, marginBottom: 20 }}>Your table's waiting.</div>

          <ModalLabel>Party size</ModalLabel>
          <div style={{ display: 'flex', gap: 6, marginBottom: 18 }}>
            {[4,6,8,10,'12+'].map(n => (
              <button key={n} onClick={() => setParty(n)} style={{
                flex: 1, padding: '12px 0', borderRadius: 8,
                background: party === n ? accent : 'transparent',
                color: party === n ? '#0a0410' : 'var(--bone)',
                border: '1px solid ' + (party === n ? accent : 'rgba(237,228,211,0.2)'),
                fontFamily: 'var(--serif)', fontSize: 15, cursor: 'pointer',
              }}>{n}</button>
            ))}
          </div>

          <ModalLabel>Day</ModalLabel>
          <div style={{ display: 'flex', gap: 6, marginBottom: 18 }}>
            {days.map(d => (
              <button key={d} onClick={() => setDay(d)} style={{
                flex: 1, padding: '12px 0', borderRadius: 8,
                background: day === d ? 'rgba(255,46,136,0.14)' : 'transparent',
                color: day === d ? 'var(--bone)' : 'var(--bone-dim)',
                border: '1px solid ' + (day === d ? accent : 'rgba(237,228,211,0.15)'),
                fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.1em', cursor: 'pointer',
              }}>{d}</button>
            ))}
          </div>

          <ModalLabel>Time</ModalLabel>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 22 }}>
            {times.map(t => (
              <button key={t} onClick={() => setTime(t)} style={{
                padding: '12px 0', borderRadius: 8,
                background: time === t ? '#1a0510' : 'transparent',
                color: time === t ? 'var(--bone)' : 'var(--bone-dim)',
                border: '1px solid ' + (time === t ? accent : 'rgba(237,228,211,0.15)'),
                fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.1em', cursor: 'pointer',
              }}>{t}</button>
            ))}
          </div>

          <a href="https://bookings.theoldschoolyard.com/book/tosy/table-inside" target="_blank" rel="noopener" style={{ display: 'block', textAlign: 'center', textDecoration: 'none', boxSizing: 'border-box',
            width: '100%', padding: '16px 0', borderRadius: 8,
            background: accent, color: '#fff',
            border: 'none', fontFamily: 'var(--mono)', fontSize: 11,
            letterSpacing: '0.35em', fontWeight: 700, cursor: 'pointer',
          }}>BOOK A TABLE INSIDE</a>
          <a href="https://bookings.theoldschoolyard.com/book/tosy/table-outside" target="_blank" rel="noopener" style={{ display: 'block', textAlign: 'center', textDecoration: 'none', boxSizing: 'border-box',
            width: '100%', padding: '13px 0', borderRadius: 8, marginTop: 8,
            background: 'transparent', color: 'var(--bone)',
            border: '1px solid rgba(234,242,255,0.25)', fontFamily: 'var(--mono)', fontSize: 10,
            letterSpacing: '0.3em', fontWeight: 700, cursor: 'pointer',
          }}>BOOK ON THE TERRACE</a>
          <div style={{ marginTop: 10, textAlign: 'center', fontFamily: 'var(--mono)', fontSize: 8, letterSpacing: '0.14em', color: 'rgba(234,242,255,0.45)' }}>LIVE AVAILABILITY · BOOKINGS.THEOLDSCHOOLYARD.COM</div>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────
// MenuModal — real menu data transcribed from the printed
// "Vol No.1" school-book menu, with the book itself kept as a
// swipeable novelty at the bottom (page scans: uploads/menu/ -> built to img/menu/).
// ─────────────────────────────────────────────────────────────
const MENU_BOOK = {
  drinks: Array.from({ length: 16 }, (_, i) => 'img/menu/d' + String(i + 1).padStart(2, '0') + '.webp'),
  food: ['img/menu/f01.webp', 'img/menu/f02.webp'],
};

// Transcribed from the printed menu — sections are the book's "subjects"
const MENU_COCKTAILS = [
  { section: 'Geography', items: [
    ['Manhattan', 9.5, 'Whiskey, vermouth & aromatic bitters — the king of whiskey cocktails'],
    ['Mojito', 9.5, 'Rum, fresh mint, lime and sugar'],
    ['Caipirinha', 8.5, 'Cachaça, fresh lime and sugar, straight from Brazil'],
    ['Pisco Sour', 9.5, 'Pisco — Chile and Peru’s answer to brandy'],
    ['Bloody Mary', 9.5, 'The great hangover cure, with Grey Goose vodka'],
    ['Tatanka', 8.5, 'Bison grass vodka, apple juice & grated cinnamon'],
    ['Irish Coffee', 8.5, 'The bestselling coffee cocktail, with a large Irish whiskey'],
    ['Paloma', 8.5, 'Tequila on the rocks, topped with grapefruit soda'],
    ['Singapore Sling', 9.5, 'Cherry and pineapple bring this gin classic to life'],
  ]},
  { section: 'History', items: [
    ['Mint Julep', 9.5, 'Mint, bourbon and sugar — we recommend Woodford Reserve'],
    ['Xtreme Martini', 11.5, 'Check out the size of our Martini — it’s HUGE'],
    ['Moscow Mule', 8.5, 'Vodka, lime & ginger beer with fresh ginger for extra kick'],
    ['Mai Tai', 9.5, 'Bacardi 8-year rum — sweet, powerful, out of this world'],
    ['Long Island Iced Tea', 10.5, 'Four spirits — aka the “Long Lane Iced Tea”'],
    ['Cosmopolitan', 9.5, '42 Below vodka and fresh berries'],
  ]},
  { section: 'Biology', items: [
    ['Painkiller', 9.5, 'A sip of paradise with a dash of island magic'],
    ['June Bug', 9.5, 'Fresh pineapple juice — zesty, tangy, sun-kissed'],
    ['Espresso Martini', 9.5, 'High caffeine + 42 Below Manuka Honey vodka'],
  ]},
  { section: 'Chemistry', items: [
    ['Pornstar Martini', 10.5, 'Passion fruit & vanilla, shot of Champagne on the side'],
    ['Atomic Agave', 10.5, 'Smoke and sugar harmonise tequila and its nectar'],
    ['Man on Fire', 10.5, 'Flamed rosemary whiskey — JD No.7 £10.50 / Single Barrel £11.50'],
    ['Fizzy Reactions', 10.5, 'Your bubbly, upgraded: Kir Royale · Bellini · Rossini · Champagne cocktail'],
  ]},
  { section: 'English', items: [
    ['Robin and Marian', 10.5, 'Grey Goose Poire vodka & English green apple'],
    ['Hemingway Daiquiri', 10.5, 'Subtly sweetened with cherry and grapefruit'],
    ['Pimm’s Cup', 8.5, 'A real English classic, packed with Borough Market fruit'],
  ]},
];

const MENU_WINE_BEER = [
  { section: 'Bubblys', items: [
    ['Prosecco', null, 'Bottle £32 · glass £8.50'],
    ['Veuve Clicquot', 75, null],
    ['Laurent Perrier Rosé', 105, null],
    ['Ruinart Blanc de Blancs', 130, null],
  ]},
  { section: 'Wines', items: [
    ['House Red', null, '£6 / £8 / £22'],
    ['House White', null, '£6 / £8 / £22'],
    ['House Rosé', null, '£6.50 / £8.50 / £22.50'],
    ['Guest wines', null, 'Please ask — updated regularly'],
  ]},
  // Two taps only — confirmed by the client 14 Aug. Kept as its own section
  // rather than mixed in with the bottles, because pint/half pricing reads
  // wrong next to a single bottle price.
  { section: 'Draught', items: [
    ['Harbour Lager', null, 'Pint £6.50 · Half £3.50 — £5 / £2.50 at Happy Hour'],
    ['Harbour IPA', null, 'Pint £6.50 · Half £3.50 — £5 / £2.50 at Happy Hour'],
  ]},
  { section: 'Bottles & Cider', items: [
    ['Becks', 5, null],
    ['Estrella Damm', 5, null],
    ['Corona', 5, null],
    ['Peroni', 5, null],
    ['Punk IPA', 5.5, null],
    ['Heineken 0.0%', 4.5, null],
    ['Punk AF (non-alcoholic)', 4.5, null],
    ['Old Mout Cider', 6, 'Various flavours'],
  ]},
  { section: 'Softs', items: [
    ['Juices, Coca-Cola, lemonade, ginger ale & more', 3, null],
    ['Red Bull', 3.5, null],
  ]},
];

const fmtPrice = p => p == null ? '' : '£' + (Number.isInteger(p) ? p : p.toFixed(2));

// The book itself — kept as a novelty flipper (used at the bottom of the sheet)
function MenuBook({ pages, height = 300 }) {
  const [page, setPage] = React.useState(0);
  const scRef = React.useRef(null);
  const onScroll = e => setPage(Math.round(e.target.scrollLeft / e.target.clientWidth));
  const turn = dir => {
    const el = scRef.current; if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth, behavior: 'smooth' });
  };
  return (
    <div>
      <div style={{ position: 'relative' }}>
        <div ref={scRef} onScroll={onScroll} className="no-scrollbar" style={{
          display: 'flex', overflowX: 'auto', scrollSnapType: 'x mandatory',
          borderRadius: 12, background: '#0d1226', height,
          WebkitOverflowScrolling: 'touch',
        }}>
          {pages.map((src, i) => (
            <div key={src} style={{ width: '100%', height: '100%', flexShrink: 0, scrollSnapAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img src={src} loading={i < 2 ? 'eager' : 'lazy'}
                alt={'Menu page ' + (i + 1) + ' of ' + pages.length}
                style={{ maxWidth: '100%', maxHeight: '100%', display: 'block' }} />
            </div>
          ))}
        </div>
        {page > 0 && (
          <button onClick={() => turn(-1)} aria-label="Previous page" style={{
            position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)',
            width: 34, height: 34, borderRadius: '50%', border: 'none', cursor: 'pointer',
            background: 'rgba(7,13,28,0.65)', color: '#fff', fontSize: 16, lineHeight: 1,
          }}>‹</button>
        )}
        {page < pages.length - 1 && (
          <button onClick={() => turn(1)} aria-label="Next page" style={{
            position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
            width: 34, height: 34, borderRadius: '50%', border: 'none', cursor: 'pointer',
            background: 'rgba(7,13,28,0.65)', color: '#fff', fontSize: 16, lineHeight: 1,
          }}>›</button>
        )}
        <div style={{
          position: 'absolute', bottom: 10, right: 10, padding: '4px 9px', borderRadius: 100,
          background: 'rgba(7,13,28,0.7)', color: 'var(--bone)', fontFamily: 'var(--mono)', fontSize: 8.5, letterSpacing: '0.14em',
        }}>{page + 1} / {pages.length}</div>
      </div>
      <div style={{ marginTop: 6, textAlign: 'center', fontFamily: 'var(--mono)', fontSize: 8, letterSpacing: '0.16em', color: 'rgba(234,242,255,0.5)' }}>
        SWIPE TO TURN THE PAGE
      </div>
    </div>
  );
}

// Text menu sections (real data), with the book as a novelty at the bottom
function MenuList({ sections, accent }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {sections.map(sec => (
        <div key={sec.section}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.3em', color: accent, marginBottom: 10, textTransform: 'uppercase' }}>{sec.section}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
            {sec.items.map(([name, price, desc]) => (
              <div key={name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: 'var(--display)', fontWeight: 800, letterSpacing: '-0.02em', fontSize: 15.5, lineHeight: 1.2, color: 'var(--bone)' }}>{name}</div>
                  {desc && <div style={{ fontFamily: 'var(--sans)', fontSize: 12, color: 'var(--bone-dim)', marginTop: 2 }}>{desc}</div>}
                </div>
                {price != null && <div style={{ fontFamily: 'var(--serif)', fontSize: 17, color: accent, flexShrink: 0 }}>{fmtPrice(price)}</div>}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function MenuModal({ open, onClose, accent = '#ff2e88' }) {
  const [tab, setTab] = React.useState('cocktails');
  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <div style={{ color: 'var(--bone)' }}>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.3em', color: accent, marginBottom: 6 }}>THE MENU</div>
        <h2 style={{ fontFamily: 'var(--display)', fontWeight: 800, letterSpacing: '-0.03em', fontSize: 26, lineHeight: 1, margin: '0 0 14px' }}>Straight from the bar.</h2>

        <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '1px solid rgba(237,228,211,0.12)' }}>
          {[['cocktails', 'Cocktails'], ['wine', 'Wine & Beer'], ['food', 'Food']].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} style={{
              padding: '10px 14px', background: 'transparent', border: 'none',
              borderBottom: '1px solid ' + (tab === k ? accent : 'transparent'),
              color: tab === k ? accent : 'var(--bone-dim)',
              fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.2em',
              cursor: 'pointer', marginBottom: -1,
            }}>{l.toUpperCase()}</button>
          ))}
        </div>

        {tab === 'cocktails' && (
          <>
            <div style={{
              marginBottom: 16, padding: '10px 14px', borderRadius: 10,
              border: '1px solid ' + accent + '44', background: accent + '14',
              fontFamily: 'var(--sans)', fontSize: 12.5, lineHeight: 1.5, color: 'var(--bone)',
            }}>
              <strong>Happy Hour til 7pm:</strong> two menu cocktails £10 · two Becks £5 · pints from £5.
            </div>
            <MenuList sections={MENU_COCKTAILS} accent={accent} />
            <div style={{
              marginTop: 16, padding: '10px 14px', borderRadius: 10,
              border: '1px solid rgba(234,242,255,0.15)', background: 'rgba(255,255,255,0.03)',
              fontFamily: 'var(--sans)', fontSize: 12.5, lineHeight: 1.5, color: 'var(--bone-dim)',
            }}>
              <strong style={{ color: 'var(--bone)' }}>Rum Punch — £22.</strong> A pitcher of Bacardi 8-year rum and tropical juices. Perfect for parties.
            </div>
            <SiteLink slug="cocktail-bar-london-bridge/"
              title="More about the cocktail bar"
              note="The full story on the bar, the menu and the makers" />
          </>
        )}

        {tab === 'wine' && (
          <>
            <MenuList sections={MENU_WINE_BEER} accent={accent} />
            <div style={{ marginTop: 14, fontFamily: 'var(--sans)', fontSize: 12, color: 'var(--bone-dim)', lineHeight: 1.5 }}>
              Full spirits &amp; liqueurs list — flip to pages 13–14 of the book below, or ask at the bar.
            </div>
          </>
        )}

        {tab === 'food' && (
          <>
            <div style={{
              marginBottom: 12, padding: '10px 14px', borderRadius: 10,
              border: '1px solid rgba(234,242,255,0.15)', background: 'rgba(255,255,255,0.03)',
              fontFamily: 'var(--sans)', fontSize: 12.5, lineHeight: 1.5, color: 'var(--bone-dim)',
            }}>
              Food is by <strong style={{ color: 'var(--bone)' }}>SMOK'D</strong> — a separate kitchen, not ours.
              Order and pay them direct through the ordering link below. Wait times are down to their kitchen,
              so allow a bit longer when we're busy. Kitchen hours on the night.
            </div>
            <a href="https://mylightspeed.app/TPBUYRQY/C-ordering/menu" target="_blank" rel="noreferrer" style={{
              display: 'block', textAlign: 'center', padding: '15px 0', borderRadius: 8, marginBottom: 18,
              background: accent, color: '#fff', textDecoration: 'none',
              fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.3em', fontWeight: 700,
            }}>ORDER FROM SMOK'D</a>
            <MenuBook pages={MENU_BOOK.food} height={440} />
          </>
        )}

        {tab !== 'food' && (
          <div style={{ marginTop: 22 }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.3em', color: 'var(--bone-dim)', marginBottom: 10 }}>PREFER THE ORIGINAL? FLIP THE BOOK ↓</div>
            <MenuBook pages={MENU_BOOK.drinks} height={300} />
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────
// Karaoke sheet — live availability AND live pricing from the booking system.
// Deliberately no prices in this file, not even in a comment: the numbers that
// were written here once already went stale, and a stale comment is how a
// hardcoded price gets reintroduced. The engine is the only source of truth —
// session lengths are derived from the slots, amounts and their labels come
// from /pricing-options and are rendered verbatim.
// Session structure per the booking-side handover (10 Aug 2026):
//   Tue/Wed  — 1 or 2-hr sessions, starts every 30 min 5:30–10pm.
//   Thu–Sat  — 2-hr sessions 5:45 / 8:00 / 10:15 (ends 12:15am).
//   1–14 Dec — min-spend override, any day.
// ─────────────────────────────────────────────────────────────
const KARAOKE_BOOKING_TYPE_ID = '00000000-0000-0000-0000-000000000012';
const BOOKINGS_BASE = 'https://bookings.theoldschoolyard.com';

// Real rooms, per the booking system — fallback when the API is unreachable.
const KARAOKE_ROOMS = [
  { name: 'Karaoke Room 1',     cap: 20 },
  { name: 'Karaoke Room 2',     cap: 20 },
  { name: 'Karaoke Room 3',     cap: 20 },
  { name: 'Karaoke Studio',     cap: 20 },
  { name: 'Small Karaoke Room', cap: 8 },
];

const STUDIO_BOOKING_TYPE_ID = '00000000-0000-0000-0000-000000000017';

function KaraokeModal({ open, onClose, accent = '#ff2e88' }) {
  const [avail, setAvail] = React.useState(null);
  const [failed, setFailed] = React.useState(false);
  // Live pricing from the engine — labels/descriptions rendered verbatim so
  // the app can never drift from what the widget charges.
  const [pricing, setPricing] = React.useState(null);   // [{ mins, pp_pence }]
  const [minSpend, setMinSpend] = React.useState(null); // { amount_pence, description }
  const [studio, setStudio] = React.useState(null);     // { firstTime, pp_pence }

  // Today if we're open (Tue–Sat), otherwise the next open day
  const target = React.useMemo(() => {
    const d = new Date();
    while (d.getDay() < 2) d.setDate(d.getDate() + 1); // Sun(0)/Mon(1) → Tue
    return d;
  }, [open]);
  // The day we ended up pricing — see findDay below. Falls back to `target`
  // until the engine has answered.
  const [dateUsed, setDateUsed] = React.useState(null);
  const shown = dateUsed || target;
  const isToday = shown.toDateString() === new Date().toDateString();
  const dayName = shown.toLocaleDateString('en-GB', { weekday: 'long' });
  const dayLabel = isToday ? 'TONIGHT' : dayName.toUpperCase() + ' ' + shown.getDate();

  React.useEffect(() => {
    if (!open) return;
    let dead = false;
    setAvail(null); setFailed(false); setPricing(null); setMinSpend(null); setStudio(null); setDateUsed(null);
    const api = BOOKINGS_BASE + '/api/tosy';
    const isoOf = d => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    const nextOpen = d => { const n = new Date(d); do { n.setDate(n.getDate() + 1); } while (n.getDay() < 2); return n; };

    // Walk forward to the first day that still has sessions. The engine stops
    // offering a slot once its start time has passed, so from about 10pm the
    // current day comes back empty — and the sheet used to just drop its whole
    // pricing block, at exactly the hour people are looking for somewhere to
    // sing. Whatever day this lands on is what dayLabel then names.
    const findDay = (d, hops) => fetch(api + '/availability?bookingTypeId=' + KARAOKE_BOOKING_TYPE_ID + '&date=' + isoOf(d) + '&partySize=4')
      .then(r => r.json())
      .then(j => ((j.slots || []).length || hops >= 3) ? { d, j } : findDay(nextOpen(d), hops + 1));

    findDay(target, 0)
      .then(({ d, j }) => {
        if (dead) return;
        setAvail(j);
        setDateUsed(d);
        const iso = isoOf(d);
        // offered session lengths, derived from the slots themselves
        const durs = new Set();
        (j.slots || []).forEach(s => {
          const step = s.durationStepMinutes || 60;
          for (let x = s.minDurationMinutes; x <= s.maxDurationMinutes; x += step) durs.add(x);
        });
        const durList = [...durs].sort((a, b) => a - b).slice(0, 3);
        return Promise.all(durList.map(x =>
          fetch(api + '/booking-types/' + KARAOKE_BOOKING_TYPE_ID + '/pricing-options?booking_date=' + iso + '&party_size=4&duration_minutes=' + x)
            .then(r => r.json()).then(p => ({ mins: x, p })).catch(() => null)
        ));
      })
      .then(list => {
        if (dead || !list) return;
        const ok = list.filter(x => x && x.p && x.p.enabled);
        setPricing(ok.filter(x => x.p.perPerson).map(x => ({ mins: x.mins, pp_pence: x.p.perPerson.pp_pence })));
        const ms = ok.find(x => x.p.minSpend);
        if (ms) setMinSpend(ms.p.minSpend);
      })
      .catch(() => { if (!dead) setFailed(true); });

    // Studio tickets — shared sessions, separate product from private rooms.
    // Resolved the same way, so the sheet never mixes two different days.
    const studioFind = (d, hops) => fetch(api + '/availability?bookingTypeId=' + STUDIO_BOOKING_TYPE_ID + '&date=' + isoOf(d) + '&partySize=2')
      .then(r => r.json())
      .then(j => ((j.slots || []).length || hops >= 3) ? { d, j } : studioFind(nextOpen(d), hops + 1));

    studioFind(target, 0)
      .then(({ d, j }) => {
        const iso = isoOf(d);
        const first = j.slots && j.slots[0];
        if (!first) return null;
        return fetch(api + '/booking-types/' + STUDIO_BOOKING_TYPE_ID + '/pricing-options?booking_date=' + iso + '&party_size=2&duration_minutes=' + first.minDurationMinutes)
          .then(r => r.json())
          .then(p => {
            // fixed-price types return { enabled:false, payment: {...} }
            const pp = p && (p.perPerson ? p.perPerson.pp_pence : (p.payment && p.payment.per_person ? p.payment.pp_pence : null));
            return { firstTime: first.startTime, pp_pence: pp };
          });
      })
      .then(s => { if (!dead && s) setStudio(s); })
      .catch(() => {});

    return () => { dead = true; };
  }, [open]);

  const fmt = t => { const [h, m] = t.split(':').map(Number); const h12 = ((h + 11) % 12) + 1; return h12 + (m ? ':' + String(m).padStart(2, '0') : '') + (h >= 12 ? 'pm' : 'am'); };

  return (
    <Modal open={open} onClose={onClose}>
      <div style={{ color: 'var(--bone)' }}>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.3em', color: accent, marginBottom: 6 }}>5 PRIVATE KARAOKE ROOMS</div>
        <h2 style={{ fontFamily: 'var(--display)', fontWeight: 800, letterSpacing: '-0.03em', fontSize: 26, lineHeight: 1, margin: '0 0 14px' }}>Your own<br/>private stage.</h2>

        <div style={{ height: 180, borderRadius: 12, overflow: 'hidden', marginBottom: 16 }}>
          <PhotoCycle srcs={window.TOSY_SETS ? TOSY_SETS.stage : []} tone={0.35} interval={4200} lyric="Take me to the place where you go…" />
        </div>

        <div style={{ fontFamily: 'var(--sans)', fontSize: 13, color: 'var(--bone-dim)', marginBottom: 16, lineHeight: 1.5 }}>
          Your own room, your own crowd, nobody watching but your mates.
          Bring the whole crew — there's a room that fits, up to 20 of you.
        </div>

        {/* what you get */}
        <div style={{ display: 'grid', gap: 8, marginBottom: 16 }}>
          {/* facts per venue: streams any YouTube karaoke version; curated
              categories per theoldschoolyard.com/karaoke-rooms-london */}
          {[
            ['Songs', 'Millions of tracks streamed online — if it has a karaoke version, you can sing it'],
            ['Curated picks', '6,000+ curated songs across 14 categories — 80s, 90s, disco, rock, hip-hop, R&B, musical theatre, big ballads, country, new hits'],
            ['Pick & queue', 'Touchscreen in your room — line up your songs as you go'],
            ['Drinks', 'Order straight from the touchscreen and they come to the room — no queuing at the bar'],
            ['Comfort', 'Air-conditioned rooms for groups of all sizes — up to 20 at a squeeze'],
          ].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', gap: 12, fontSize: 13, borderTop: '1px solid rgba(234,242,255,0.1)', paddingTop: 8 }}>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.14em', color: accent, width: 78, flexShrink: 0, paddingTop: 2, textTransform: 'uppercase' }}>{k}</span>
              <span style={{ fontFamily: 'var(--sans)', color: 'var(--bone)' }}>{v}</span>
            </div>
          ))}
        </div>

        {/* Pricing rendered live from the engine's pricing-options endpoint */}
        {pricing && pricing.length > 0 ? (
          <div style={{
            marginBottom: 16, padding: '12px 14px', borderRadius: 10,
            border: '1px solid ' + accent + '44', background: accent + '10',
          }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 8.5, letterSpacing: '0.2em', color: accent, marginBottom: 6 }}>PRICING {dayLabel}</div>
            <div style={{ fontFamily: 'var(--sans)', fontSize: 13.5, color: 'var(--bone)', lineHeight: 1.6 }}>
              {pricing.map(p => (p.mins / 60) + ' hr — £' + (p.pp_pence / 100).toFixed(2).replace(/\.00$/, '') + ' per person').join('  ·  ')}
            </div>
            {minSpend && (
              <div style={{ fontFamily: 'var(--sans)', fontSize: 12, color: 'var(--bone-dim)', marginTop: 6, lineHeight: 1.5 }}>
                Or a <strong style={{ color: 'var(--bone)' }}>£{(minSpend.amount_pence / 100).toFixed(0)} minimum spend</strong> behind the bar instead — {minSpend.description}. Amount varies with group size, confirmed as you book.
              </div>
            )}
          </div>
        ) : (
          <div style={{
            marginBottom: 16, padding: '10px 14px', borderRadius: 10,
            border: '1px solid rgba(234,242,255,0.15)', background: 'rgba(255,255,255,0.03)',
            fontFamily: 'var(--sans)', fontSize: 12.5, lineHeight: 1.5, color: 'var(--bone-dim)',
          }}>
            Pick a room and time below — session lengths and prices are shown as you book.
          </div>
        )}

        {/* one clear action — the widget handles rooms and times */}
        <a href={BOOKINGS_BASE + '/book/tosy/karaoke-room'} target="_blank" rel="noreferrer" style={{
          display: 'block', textAlign: 'center', padding: '16px 0', borderRadius: 8,
          background: accent, color: '#fff', textDecoration: 'none',
          fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.3em', fontWeight: 700,
        }}>BOOK A KARAOKE ROOM</a>

        {/* Studio tickets — shared sessions, the last-minute option */}
        <div style={{
          marginTop: 16, padding: 14, borderRadius: 12,
          border: '1px solid rgba(46,155,255,0.45)', background: 'rgba(46,155,255,0.08)',
        }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 8.5, letterSpacing: '0.2em', color: '#2e9bff', marginBottom: 6 }}>THE STUDIO · SHARED SESSION</div>
          <div style={{ fontFamily: 'var(--display)', fontWeight: 800, letterSpacing: '-0.02em', fontSize: 18, lineHeight: 1.05, marginBottom: 6 }}>Last-minute? Grab studio spots.</div>
          <div style={{ fontFamily: 'var(--sans)', fontSize: 12.5, color: 'var(--bone-dim)', lineHeight: 1.5, marginBottom: 12 }}>
            <strong style={{ color: 'var(--bone)' }}>Not a private room</strong> — studio tickets are per-person spots in our big shared Karaoke Studio, singing alongside other groups. Book online in two minutes, even when we can't get to the phone.
            {' '}From <strong style={{ color: 'var(--bone)' }}>£{studio && studio.pp_pence != null ? (studio.pp_pence / 100).toFixed(2).replace(/\.00$/, '') : '5'} per person per hour</strong>.
            {studio && studio.firstTime && <> Sessions from {fmt(studio.firstTime)}.</>}
          </div>
          <a href={BOOKINGS_BASE + '/book/tosy/studio-tickets'} target="_blank" rel="noreferrer" style={{
            display: 'block', textAlign: 'center', padding: '13px 0', borderRadius: 8,
            background: '#2e9bff', color: '#fff', textDecoration: 'none',
            fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.25em', fontWeight: 700,
          }}>GET STUDIO TICKETS</a>
        </div>

        {/* Song inspiration — the live blog series on theoldschoolyard.com */}
        <div style={{ marginTop: 18 }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.3em', color: 'var(--bone-dim)', marginBottom: 10 }}>NEED SONG IDEAS?</div>
          <a href="https://theoldschoolyard.com/karaoke-song-list/" target="_blank" rel="noopener" style={{
            display: 'block', padding: '12px 14px', borderRadius: 10, marginBottom: 8,
            border: '1px solid rgba(255,210,63,0.4)', background: 'rgba(255,210,63,0.08)',
            color: 'var(--bone)', textDecoration: 'none',
          }}>
            <span style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 14.5, letterSpacing: '-0.02em' }}>The Ultimate Karaoke Song List →</span>
            <span style={{ display: 'block', fontFamily: 'var(--sans)', fontSize: 11.5, color: 'var(--bone-dim)', marginTop: 2 }}>6,000+ curated songs, organised by decade, genre and occasion</span>
          </a>
          <div className="no-scrollbar" style={{ display: 'flex', gap: 6, overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: 2 }}>
            {[
              ['All-time greats', 'most-popular-karaoke-songs'],
              ['Hen party', 'hen-party-karaoke-songs'],
              ['80s', 'best-80s-karaoke-songs'],
              ['90s', 'best-90s-karaoke-songs'],
              ['Duets', 'best-karaoke-duets'],
              ['Rock', 'rock-karaoke-classics'],
              ['Big ballads', 'karaoke-ballads'],
              ['Disco', 'disco-karaoke-songs'],
              ['R&B', 'rnb-karaoke-songs'],
              ['Hip hop', 'hip-hop-karaoke-songs'],
              ['Musical theatre', 'musical-theatre-karaoke'],
              ['New hits', 'new-karaoke-hits'],
              ['Country', 'country-karaoke-songs'],
            ].map(([label, slug]) => (
              <a key={slug} href={'https://theoldschoolyard.com/' + slug + '/'} target="_blank" rel="noopener" style={{
                flexShrink: 0, padding: '8px 12px', borderRadius: 100,
                border: '1px solid rgba(234,242,255,0.2)', color: 'var(--bone)',
                textDecoration: 'none', fontFamily: 'var(--mono)', fontSize: 9.5, letterSpacing: '0.1em',
              }}>{label.toUpperCase()}</a>
            ))}
          </div>
        </div>

        <SiteLink slug="karaoke-rooms-london/" accent="#ff2e88"
          title="All about our karaoke rooms"
          note="Room sizes, song library and everything else, on the main site" />

        <div style={{ marginTop: 12, textAlign: 'center', fontFamily: 'var(--mono)', fontSize: 8, letterSpacing: '0.14em', color: 'rgba(234,242,255,0.45)' }}>
          BOOK ONLINE · BOOKINGS.THEOLDSCHOOLYARD.COM
        </div>
      </div>
    </Modal>
  );
}

// Rich detail sheet for the terrace — photos front and centre, booking
// deep link to the outside-table widget.
function TerraceModal({ open, onClose, accent = '#ff2e88' }) {
  if (!open) return null;
  const facts = [
    ['Weatherproof', 'Heated and covered — open year-round, whatever London throws at it'],
    ['When', 'Every night we’re open, Tue–Sat'],
    ['Booking', 'First come first served — or reserve a terrace table ahead'],
    ['Drinks', 'Full bar service · Happy Hour til 7pm'],
  ];
  return (
    <Modal open={open} onClose={onClose}>
      <div style={{ color: 'var(--bone)' }}>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.3em', color: accent, marginBottom: 6 }}>THE TERRACE</div>
        <h2 style={{ fontFamily: 'var(--display)', fontWeight: 800, letterSpacing: '-0.03em', fontSize: 26, lineHeight: 1, margin: '0 0 14px' }}>Heated. Covered.<br/>Year-round.</h2>

        <div style={{ height: 200, borderRadius: 12, overflow: 'hidden', marginBottom: 16 }}>
          <PhotoCycle srcs={window.TOSY_SETS ? TOSY_SETS.terrace : []} tone={0.3} interval={4200} />
        </div>

        <div style={{ fontFamily: 'var(--sans)', fontSize: 13, color: 'var(--bone-dim)', lineHeight: 1.55, marginBottom: 16 }}>
          Our front terrace on Long Lane — heaters on, cover overhead, drinks flowing.
          The best seat in SE1 for watching the world go by, whatever the weather.
        </div>

        {/* 220, not 170: this set includes a tall portrait shot, and a 2.2:1
            letterbox threw away two thirds of it — see TOSY_FOCUS in media.jsx */}
        <div style={{ height: 220, borderRadius: 12, overflow: 'hidden', marginBottom: 16 }}>
          <PhotoCycle srcs={window.TOSY_SETS ? TOSY_SETS.terrace2 : []} tone={0.3} interval={5200} />
        </div>

        <div style={{ display: 'grid', gap: 8, marginBottom: 20 }}>
          {facts.map(([k, v]) => (
            <div key={k} style={{ display: 'flex', gap: 12, fontSize: 13, borderTop: '1px solid rgba(234,242,255,0.1)', paddingTop: 8 }}>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.14em', color: accent, width: 100, flexShrink: 0, paddingTop: 2, textTransform: 'uppercase' }}>{k}</span>
              <span style={{ fontFamily: 'var(--sans)', color: 'var(--bone)' }}>{v}</span>
            </div>
          ))}
        </div>

        <a href="https://bookings.theoldschoolyard.com/book/tosy/table-outside" target="_blank" rel="noopener" style={{
          display: 'block', textAlign: 'center', padding: '16px 0', borderRadius: 8,
          background: accent, color: '#fff', textDecoration: 'none',
          fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.3em', fontWeight: 700,
        }}>BOOK A TERRACE TABLE</a>

        <SiteLink slug="beer-garden-london-bridge/"
          title="More on the beer garden & terrace"
          note="Photos, layout and what it's like year-round" />
      </div>
    </Modal>
  );
}

// Rich detail sheet for happy hour / tonight's offers — same pattern as
// the masterclass and event sheets. Happy hour is walk-in, so the CTA
// hands over to the menu rather than the booking system.
function OffersModal({ open, onClose, onViewMenu, accent = '#ff2e88' }) {
  if (!open) return null;
  const facts = [
    ['When', 'Tuesday – Saturday, open until 7pm'],
    ['Cocktails', 'Two menu cocktails for £10'],
    ['Beers', 'Two bottles of Becks for £5'],
    ['Pints', 'From £5'],
    ['Booking', 'None needed — walk straight in'],
  ];
  return (
    <Modal open={open} onClose={onClose}>
      <div style={{ color: 'var(--bone)' }}>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.3em', color: accent, marginBottom: 6 }}>TONIGHT'S OFFERS</div>
        <h2 style={{ fontFamily: 'var(--display)', fontWeight: 800, letterSpacing: '-0.03em', fontSize: 26, lineHeight: 1, margin: '0 0 14px' }}>Happy Hour, every open day.</h2>

        <div style={{ height: 150, borderRadius: 12, overflow: 'hidden', marginBottom: 16 }}>
          <PhotoCycle srcs={window.TOSY_SETS ? TOSY_SETS.cocktails : []} tone={0.35} interval={4500} />
        </div>

        <div style={{ fontFamily: 'var(--sans)', fontSize: 13, color: 'var(--bone-dim)', lineHeight: 1.55, marginBottom: 16 }}>
          Get here before 7 and the first round's the easy part. Runs every day
          we're open — perfect warm-up before your karaoke slot.
        </div>

        <div style={{ display: 'grid', gap: 8, marginBottom: 20 }}>
          {facts.map(([k, v]) => (
            <div key={k} style={{ display: 'flex', gap: 12, fontSize: 13, borderTop: '1px solid rgba(234,242,255,0.1)', paddingTop: 8 }}>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.14em', color: accent, width: 92, flexShrink: 0, paddingTop: 2, textTransform: 'uppercase' }}>{k}</span>
              <span style={{ fontFamily: 'var(--sans)', color: 'var(--bone)' }}>{v}</span>
            </div>
          ))}
        </div>

        <button onClick={onViewMenu} style={{
          display: 'block', width: '100%', textAlign: 'center', padding: '16px 0', borderRadius: 8,
          background: accent, color: '#fff', border: 'none', cursor: 'pointer',
          fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.3em', fontWeight: 700,
        }}>SEE THE COCKTAIL MENU</button>
        <a href="https://maps.google.com/?q=The+Old+School+Yard,+111+Long+Lane,+London+SE1+4PH" target="_blank" rel="noreferrer" style={{
          display: 'block', textAlign: 'center', padding: '13px 0', borderRadius: 8, marginTop: 8,
          border: '1px solid rgba(234,242,255,0.22)', color: 'var(--bone)', textDecoration: 'none',
          fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.25em',
        }}>GET DIRECTIONS</a>
      </div>
    </Modal>
  );
}

// Rich detail sheet for the cocktail masterclass — same pattern as the
// event sheet: hero imagery, key-facts grid, one clear CTA into the
// booking system.
function MasterclassModal({ open, onClose, accent = '#ff2e88' }) {
  if (!open) return null;
  const facts = [
    ['Price', '£50 per person'],
    ['Length', '2 hours, hands-on behind the bar'],
    ['You make', '3–4 cocktails each'],
    ['Included', 'A 2-hour private karaoke room afterwards'],
    ['Group size', 'Minimum 6 · maximum 15'],
    ['Times', 'Classes start from 4pm'],
    ['Good to know', 'Non-alcoholic option available · not available in December'],
  ];
  return (
    <Modal open={open} onClose={onClose}>
      <div style={{ color: 'var(--bone)' }}>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.3em', color: accent, marginBottom: 6 }}>COCKTAIL MASTERCLASS</div>
        <h2 style={{ fontFamily: 'var(--display)', fontWeight: 800, letterSpacing: '-0.03em', fontSize: 26, lineHeight: 1, margin: '0 0 14px' }}>Shake it, then sing it.</h2>

        {/* 220, not 150: at 150 this was a 2.2:1 letterbox showing 34% of the
            portrait pour shot and 60% of the others. Client called all three
            over-cropped, 11 Aug. */}
        <div style={{ height: 220, borderRadius: 12, overflow: 'hidden', marginBottom: 16 }}>
          <PhotoCycle srcs={window.TOSY_SETS ? TOSY_SETS.masterclass : []} tone={0.35} interval={4500} />
        </div>

        <div style={{ fontFamily: 'var(--sans)', fontSize: 13, color: 'var(--bone-dim)', lineHeight: 1.55, marginBottom: 16 }}>
          Two hours behind the bar with our team, making (and drinking) the classics —
          then take your group straight into a private karaoke room, on us.
          Perfect for hen parties, birthdays and work socials.
        </div>

        <div style={{ display: 'grid', gap: 8, marginBottom: 20 }}>
          {facts.map(([k, v]) => (
            <div key={k} style={{ display: 'flex', gap: 12, fontSize: 13, borderTop: '1px solid rgba(234,242,255,0.1)', paddingTop: 8 }}>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.14em', color: accent, width: 92, flexShrink: 0, paddingTop: 2, textTransform: 'uppercase' }}>{k}</span>
              <span style={{ fontFamily: 'var(--sans)', color: 'var(--bone)' }}>{v}</span>
            </div>
          ))}
        </div>

        <a href="https://bookings.theoldschoolyard.com/book/tosy/cocktail-masterclass" target="_blank" rel="noopener" style={{
          display: 'block', textAlign: 'center', padding: '16px 0', borderRadius: 8,
          background: accent, color: '#fff', textDecoration: 'none',
          fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.3em', fontWeight: 700,
        }}>BOOK A MASTERCLASS</a>

        <SiteLink slug="cocktail-masterclass/"
          title="Full masterclass details"
          note="What you'll make, how it runs, and group options" />
        <a href="https://wa.me/447405289375" target="_blank" rel="noreferrer" style={{
          display: 'block', textAlign: 'center', padding: '13px 0', borderRadius: 8, marginTop: 8,
          border: '1px solid rgba(234,242,255,0.22)', color: 'var(--bone)', textDecoration: 'none',
          fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.25em',
        }}>ASK US ON WHATSAPP</a>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────
// TosyMap — SVG recreation of the venue's own area map (light
// card style): Thames, London Bridge stn, Shard, Guy's, Crosby
// Row down to Long Lane, Borough, walking arrows, TOSY dot.
// Used in the Find Us sheet.
// ─────────────────────────────────────────────────────────────
function TosyMap() {
  const road = '#b9bec4';
  const ink = '#1c1c1e';
  const blue = '#2456d6';
  const red = '#e8232a';
  return (
    <svg viewBox="0 0 340 440" style={{ width: '100%', display: 'block', borderRadius: 12, background: '#f0efeb' }}>
      {/* Thames */}
      <polygon points="0,0 340,0 340,44 0,76" fill="#29abe2" />
      {/* boat */}
      <g fill={ink} transform="translate(225 8)">
        <rect x="10" y="14" width="78" height="8" rx="2" />
        <rect x="22" y="7" width="14" height="8" /><rect x="44" y="4" width="10" height="11" />
        <rect x="60" y="7" width="16" height="8" /><rect x="47" y="0" width="2" height="6" />
      </g>
      {/* London Bridge road */}
      <path d="M62 2 L88 96" stroke={road} strokeWidth="13" strokeLinecap="round" fill="none" />
      <text x="60" y="46" fontFamily="var(--sans)" fontWeight="600" fontSize="11" fill={ink} transform="rotate(72 60 46)">London Bridge</text>
      {/* Borough High Street */}
      <path d="M88 96 Q70 240 56 436" stroke={road} strokeWidth="14" strokeLinecap="round" fill="none" />
      <text x="96" y="160" fontFamily="var(--sans)" fontWeight="600" fontSize="12" fill={ink} transform="rotate(82 96 160)">Borough High Street (A3)</text>
      {/* Tooley St */}
      <path d="M258 74 L340 66" stroke={road} strokeWidth="10" strokeLinecap="round" fill="none" />
      <text x="266" y="60" fontFamily="var(--sans)" fontWeight="700" fontSize="13" fill={ink}>Tooley St</text>
      {/* St Thomas St */}
      <path d="M120 172 L340 162" stroke={road} strokeWidth="10" strokeLinecap="round" fill="none" />
      <text x="212" y="188" fontFamily="var(--sans)" fontWeight="700" fontSize="14" fill={ink}>St Thomas St</text>
      {/* side streets, faint */}
      <path d="M250 260 Q245 310 225 372" stroke={road} strokeWidth="8" strokeLinecap="round" fill="none" opacity="0.7" />
      <text x="262" y="292" fontFamily="var(--sans)" fontWeight="600" fontSize="10" fill={ink} transform="rotate(75 262 292)">Kipling St</text>
      {/* the Shard */}
      <polygon points="212,150 228,28 244,150" fill={ink} />
      <text x="252" y="140" fontFamily="var(--sans)" fontWeight="600" fontSize="11" fill={ink}>Shard</text>
      <text x="252" y="153" fontFamily="var(--sans)" fontWeight="600" fontSize="11" fill={ink}>Building</text>
      {/* London Bridge station */}
      <text x="115" y="92" textAnchor="middle" fontFamily="var(--sans)" fontWeight="800" fontSize="15" fill={ink}>London</text>
      <text x="115" y="109" textAnchor="middle" fontFamily="var(--sans)" fontWeight="800" fontSize="15" fill={ink}>Bridge</text>
      <circle cx="104" cy="130" r="12" fill="none" stroke={red} strokeWidth="5" />
      <rect x="86" y="127" width="36" height="6" rx="3" fill={blue} />
      {/* rail symbol */}
      <g stroke={red} strokeWidth="4" fill="none">
        <path d="M132 124 L156 124" /><path d="M128 132 L152 132" />
        <path d="M136 118 L132 138" /><path d="M150 118 L146 138" />
      </g>
      {/* Guy's Hospital */}
      <text x="148" y="222" fontFamily="var(--sans)" fontWeight="700" fontSize="13" fill={ink}>Guys</text>
      <text x="140" y="237" fontFamily="var(--sans)" fontWeight="700" fontSize="13" fill={ink}>Hospital</text>
      <circle cx="238" cy="222" r="13" fill="none" stroke={ink} strokeWidth="2.5" />
      <text x="238" y="228" textAnchor="middle" fontFamily="var(--sans)" fontWeight="800" fontSize="15" fill={ink}>H</text>
      {/* Great Maze Lane / Crosby Row */}
      <path d="M196 167 Q194 230 190 290 Q188 320 184 356" stroke={road} strokeWidth="9" strokeLinecap="round" fill="none" />
      <text x="207" y="200" fontFamily="var(--sans)" fontWeight="600" fontSize="10.5" fill={ink} transform="rotate(87 207 200)">Great Maze Lane</text>
      <text x="172" y="290" fontFamily="var(--sans)" fontWeight="600" fontSize="10.5" fill={ink} transform="rotate(96 172 290)">Crosby Row</text>
      {/* Long Lane */}
      <path d="M60 388 Q170 368 340 348" stroke={road} strokeWidth="13" strokeLinecap="round" fill="none" />
      <text x="120" y="382" fontFamily="var(--sans)" fontWeight="700" fontSize="14" fill={ink} transform="rotate(-6 120 382)">Long Lane</text>
      {/* Great Dover St + red line */}
      <path d="M56 436 Q180 420 340 396" stroke={road} strokeWidth="10" strokeLinecap="round" fill="none" opacity="0.8" />
      <path d="M62 402 Q180 412 340 388" stroke={red} strokeWidth="2.5" fill="none" />
      <text x="150" y="428" fontFamily="var(--sans)" fontWeight="600" fontSize="11" fill={ink} transform="rotate(-7 150 428)">Great Dover St</text>
      {/* walking arrows */}
      <g stroke={blue} strokeWidth="4" fill="none" strokeLinecap="round">
        <path d="M142 140 Q160 150 168 164" markerEnd="url(#arrB)" />
        <path d="M76 396 Q100 388 122 382" markerEnd="url(#arrB)" />
      </g>
      <defs>
        <marker id="arrB" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
          <polygon points="0,0 7,3.5 0,7" fill={blue} />
        </marker>
      </defs>
      {/* Borough station */}
      <circle cx="52" cy="392" r="12" fill="none" stroke={red} strokeWidth="5" />
      <rect x="34" y="389" width="36" height="6" rx="3" fill={blue} />
      <text x="14" y="424" fontFamily="var(--sans)" fontWeight="800" fontSize="15" fill={ink}>Borough</text>
      {/* TOSY */}
      <circle cx="188" cy="362" r="11" fill={red} />
      <text x="204" y="352" fontFamily="var(--sans)" fontWeight="800" fontSize="12" fill="#c4185e">TOSY</text>
    </svg>
  );
}
// Everything app.jsx mounts. These used to be picked up as implicit globals —
// they worked only because this file was a plain, unwrapped script. Naming them
// explicitly makes the contract real and survives the compile step.
window.TosyMap = TosyMap;
window.SiteLink = SiteLink;
window.BookingModal = BookingModal;
window.MenuModal = MenuModal;
window.KaraokeModal = KaraokeModal;
window.TerraceModal = TerraceModal;
window.OffersModal = OffersModal;
window.MasterclassModal = MasterclassModal;
window.GenericModal = GenericModal;

function GenericModal({ open, onClose, title, subtitle, body, accent = '#ff2e88' }) {
  return (
    <Modal open={open} onClose={onClose}>
      <div style={{ color: 'var(--bone)' }}>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.3em', color: accent, marginBottom: 6 }}>{title}</div>
        <h2 style={{ fontFamily: 'var(--display)', fontWeight: 800, letterSpacing: '-0.03em', fontSize: 26, lineHeight: 1, margin: '0 0 14px' }}>{subtitle}</h2>
        <div style={{ fontFamily: 'var(--sans)', fontSize: 14, color: 'var(--bone-dim)', lineHeight: 1.6 }}>{body}</div>
      </div>
    </Modal>
  );
}

Object.assign(window, { BookingModal, MenuModal, KaraokeModal, MasterclassModal, OffersModal, TerraceModal, GenericModal });
